import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { 
  Leaf, 
  Brain, 
  Sparkles, 
  ArrowRight, 
  Compass, 
  Check, 
  UserCheck, 
  Layers,
  Database,
  Trash2,
  RefreshCw,
  ChevronRight,
  LogOut,
  Download
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { AuthProvider, ProtectedRoute, LoginPage, useAuth } from "./auth";
import { AuraQuiz } from "./AuraQuiz";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEME = {
  bg_primary: "#F9F8F6",
  bg_secondary: "#F1EDE4",
  text_primary: "#2A3B28",
  text_secondary: "#5C6B5A",
  text_muted: "#8A9B84",
  accent_primary: "#8A9A86",
  accent_primary_hover: "#748470",
  accent_secondary: "#D4A373",
  border_soft: "#E2DCD0"
};

// Home Landing Page Component
function Home() {
  const [email, setEmail] = useState("");
  const [activeOutcome, setActiveOutcome] = useState("nootropic");
  const [isLoading, setIsLoading] = useState(false);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    setIsLoading(true);
    try {
      const outcomeLabelMap = {
        nootropic: "Nootropic/Focus",
        calm: "Stress Relief/Calm",
        mood: "Mood Elevation"
      };

      const response = await axios.post(`${API}/waitlist`, {
        email: email,
        outcome_interest: outcomeLabelMap[activeOutcome] || "Unsure"
      });

      if (response.status === 201) {
        toast.success("Welcome aboard!", {
          description: "You've successfully joined the InnerAura list.",
          style: {
            background: THEME.bg_primary,
            color: THEME.text_primary,
            borderColor: THEME.border_soft,
          }
        });
        setEmail("");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const outcomes = {
    nootropic: {
      title: "Cognitive health (nootropics)",
      short: "Focus & Flow",
      tagline: "Unleash sustained mental clarity without the crash.",
      description: "Engineered with Lion's Mane, L-Theanine, and natural green tea extracts. Perfect for deep focus, enhanced working memory, and supreme neural protection.",
      benefits: [
        "Elevates alpha brain waves for flow state",
        "Supports long-term neuroplasticity",
        "Zero artificial stimulants, jitter-free energy"
      ]
    },
    calm: {
      title: "Stress relief & nervous system calm",
      short: "Serenity & Balance",
      tagline: "Decompress and lower cortisol levels naturally.",
      description: "Infused with natural Ashwagandha, Holy Basil, and Lemon Balm. Crafted to soothe overstimulated sensory pathways and establish a tranquil inner sanctuary.",
      benefits: [
        "Promotes restorative parasympathetic response",
        "Buffered with marine magnesium",
        "Reduces stress-induced fatigue instantly"
      ]
    },
    mood: {
      title: "Emotional health & mood elevation",
      short: "Vibrancy & Radiance",
      tagline: "Lift your spirits and synchronize your circadian vibes.",
      description: "Infused with Saffron, Rhodiola Rosea, and active Vitamin D3. Curated to stimulate serotonin production and bring bright, sunlit clarity to grey days.",
      benefits: [
        "Boosts natural dopamine and serotonin synthesis",
        "Includes mood-regulating Adaptogens",
        "Refreshing crisp botanical finish"
      ]
    }
  };

  return (
    <div className="min-h-screen bg-bg_primary font-body text-text_primary selection:bg-accent_primary selection:text-white">
      {/* Sticky Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#F9F8F6]/70 border-b border-white/40 shadow-[0_4px_32px_rgba(0,0,0,0.02)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="text-accent_primary h-6 w-6 stroke-[1.5]" />
            <span className="text-2xl font-headings font-normal tracking-tight">InnerAura</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-text_secondary">
            <a href="#science" className="hover:text-text_primary transition-colors">The Science</a>
            <a href="#vision" className="hover:text-text_primary transition-colors">Vision</a>
            <a href="#ingredients" className="hover:text-text_primary transition-colors">Ingredients</a>
          </nav>

          <div>
            <a 
              href="#join"
              className="bg-accent_primary hover:bg-accent_primary_hover text-white text-sm font-medium rounded-full px-6 py-2.5 transition-all duration-300 ease-out shadow-sm hover:shadow-md hover:-translate-y-0.5 inline-block"
            >
              Join Waitlist
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-36 pb-20 md:py-32 max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
        <div className="md:col-span-7 space-y-8">
          <div className="inline-flex items-center gap-2 bg-[#F1EDE4] px-4 py-1.5 rounded-full border border-border_soft/60">
            <Sparkles className="h-4 w-4 text-accent_secondary" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-text_secondary">Beverages Reimagined</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none text-text_primary font-headings">
            Align Your Health. <br />
            <span className="font-normal italic text-text_secondary">Elevate Your Aura.</span>
          </h1>

          <p className="text-lg md:text-xl font-normal leading-relaxed text-text_secondary max-w-2xl">
            InnerAura is pioneering a portfolio of natural functional beverages meticulously designed for distinct health outcomes. Crafted for cognitive clarity, deep stress relief, and natural mood elevation.
          </p>

          <form onSubmit={handleWaitlistSubmit} className="space-y-4 max-w-lg pt-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                placeholder="Enter your email to align your aura..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="waitlist-email-input"
                className="flex-grow bg-[#F1EDE4] rounded-full border border-border_soft px-6 py-4 text-text_primary placeholder:text-text_secondary/50 focus:outline-none focus:ring-2 focus:ring-accent_primary/30 transition-all text-base font-body"
              />
              <button
                type="submit"
                disabled={isLoading}
                data-testid="waitlist-submit-button"
                className="bg-[#8A9A86] hover:bg-[#748470] text-white font-medium rounded-full px-8 py-4 transition-all duration-300 ease-out shadow-sm hover:shadow-md hover:-translate-y-0.5 whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isLoading ? "Joining..." : "Join the Aura List"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-text_muted italic px-4">
              {"✨ Be first to know when we launch our generic outcome-aligned beverage pilot."}
            </p>
          </form>
        </div>

        <div className="md:col-span-5 relative">
          <div className="absolute -inset-4 bg-accent_primary/5 rounded-3xl blur-3xl -z-10"></div>
          <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border border-border_soft/30 bg-bg_secondary">
            <img 
              src="https://images.unsplash.com/photo-1643373975861-f1335411da61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWVzdGhldGljJTIwYmV2ZXJhZ2UlMjBnbGFzc3xlbnwwfHx8fDE3ODE2MzA5MTN8MA&ixlib=rb-4.1.0&q=85" 
              alt="Minimalist Functional Beverage" 
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
        </div>
      </section>

      {/* Outcome Selector Section */}
      <section id="science" className="bg-[#F1EDE4] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9B84]">Efficacy First</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-normal tracking-tight text-text_primary">
              {"Meticulously Aligned to Your Body's Essential Needs"}
            </h2>
            <p className="text-base md:text-lg text-text_secondary leading-relaxed">
              {"Why settle for generic hydration? Our beverages are strategically compiled with botanical actives targeted at your body's key biological outcome vectors."}
            </p>
          </div>

          <div className="flex justify-center gap-3 md:gap-4 mb-12 flex-wrap">
            <button
              onClick={() => setActiveOutcome("nootropic")}
              data-testid="outcome-tab-focus"
              className={`px-6 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeOutcome === "nootropic" 
                  ? "bg-text_primary text-white shadow-md" 
                  : "bg-bg_primary/60 text-text_secondary hover:bg-bg_primary border border-border_soft/60"
              }`}
            >
              🧠 Cognitive Health
            </button>
            <button
              onClick={() => setActiveOutcome("calm")}
              data-testid="outcome-tab-calm"
              className={`px-6 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeOutcome === "calm" 
                  ? "bg-text_primary text-white shadow-md" 
                  : "bg-bg_primary/60 text-text_secondary hover:bg-bg_primary border border-border_soft/60"
              }`}
            >
              🌿 Stress Relief
            </button>
            <button
              onClick={() => setActiveOutcome("mood")}
              data-testid="outcome-tab-mood"
              className={`px-6 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeOutcome === "mood" 
                  ? "bg-text_primary text-white shadow-md" 
                  : "bg-bg_primary/60 text-text_secondary hover:bg-bg_primary border border-border_soft/60"
              }`}
            >
              ✨ Emotional Health
            </button>
          </div>

          {/* Outcome Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center bg-bg_primary rounded-3xl p-8 md:p-12 border border-border_soft/60 shadow-sm">
            <div className="md:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent_secondary font-headings">
                Outcome Category
              </span>
              <h3 className="text-2xl sm:text-3xl font-headings font-normal tracking-tight text-text_primary">
                {outcomes[activeOutcome].title}
              </h3>
              <p className="text-lg font-medium text-accent_primary italic">
                {`"${outcomes[activeOutcome].tagline}"`}
              </p>
              <p className="text-base leading-relaxed text-text_secondary">
                {outcomes[activeOutcome].description}
              </p>

              <div className="space-y-3 pt-4 border-t border-border_soft">
                <span className="text-xs font-bold uppercase tracking-wider text-text_secondary">{"Targeted Botanical Benefits:"}</span>
                {outcomes[activeOutcome].benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-text_primary">
                    <Check className="h-5 w-5 text-accent_primary shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="aspect-square bg-[#E1DDD4] rounded-2xl overflow-hidden relative border border-border_soft/40">
                <img 
                  src="https://images.unsplash.com/photo-1633933329875-044a32f4837f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMGJvdGFuaWNhbCUyMGluZ3JlZGllbnRzJTIwc2FnZSUyMGdyZWVufGVufDB8fHx8MTc4MTYzMDkxM3ww&ixlib=rb-4.1.0&q=85" 
                  alt="Outcome active botanicals"
                  className="w-full h-full object-cover grayscale-[20%] sepia-[10%] hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-md py-3 px-5 rounded-xl border border-border_soft shadow-sm">
                  <span className="text-xs font-semibold text-text_secondary uppercase tracking-widest block">Active Botanicals</span>
                  <span className="text-sm font-semibold text-text_primary">{outcomes[activeOutcome].short}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ingredient Spotlight Bento Grid */}
      <section id="ingredients" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl space-y-4 mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9B84]">Tactile Botanicals</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-normal tracking-tight text-text_primary">
            The Botanical Spotlight
          </h2>
          <p className="text-base text-text_secondary leading-relaxed">
            Every molecule counts. We select our active botanicals, adaptogens, and minerals strictly based on premium natural purity and outcome potency.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1 */}
          <div className="bg-[#F1EDE4] rounded-2xl p-8 border border-border_soft/50 shadow-sm space-y-6">
            <div className="h-12 w-12 rounded-full bg-bg_primary flex items-center justify-center border border-border_soft">
              <Brain className="h-6 w-6 text-accent_primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text_primary mb-2">{"Lion's Mane Mushroom"}</h3>
              <p className="text-sm text-text_secondary leading-relaxed">
                {"A majestic cognitive adaptogen verified to stimulate Nerve Growth Factor (NGF) production, reinforcing focus and neural elasticity."}
              </p>
            </div>
            <div className="text-xs font-semibold text-accent_secondary tracking-widest uppercase">Target Outcome: Cognitive Clarity</div>
          </div>

          {/* Card 2 */}
          <div className="bg-[#F1EDE4] rounded-2xl p-8 border border-border_soft/50 shadow-sm space-y-6">
            <div className="h-12 w-12 rounded-full bg-bg_primary flex items-center justify-center border border-border_soft">
              <Compass className="h-6 w-6 text-accent_primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text_primary mb-2">{"KSM-66® Ashwagandha"}</h3>
              <p className="text-sm text-text_secondary leading-relaxed">
                {"The gold-standard natural root extract clinically documented to lower baseline serum cortisol levels, offering sustained mental tranquility."}
              </p>
            </div>
            <div className="text-xs font-semibold text-accent_secondary tracking-widest uppercase">Target Outcome: Stress Relief</div>
          </div>

          {/* Card 3 */}
          <div className="bg-[#F1EDE4] rounded-2xl p-8 border border-border_soft/50 shadow-sm space-y-6">
            <div className="h-12 w-12 rounded-full bg-bg_primary flex items-center justify-center border border-border_soft">
              <Sparkles className="h-6 w-6 text-accent_primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text_primary mb-2">Iranian Saffron Extract</h3>
              <p className="text-sm text-text_secondary leading-relaxed">
                Every synaptic junction is supported as this vibrant spice works gently to optimize neural transmission and raise baseline joy.
              </p>
            </div>
            <div className="text-xs font-semibold text-accent_secondary tracking-widest uppercase">Target Outcome: Mood Elevation</div>
          </div>
        </div>
      </section>

      {/* What's Your Aura? Interactive Quiz */}
      <AuraQuiz />

      {/* Editorial Company Vision / About */}
      <section id="vision" className="bg-[#F1EDE4] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-border_soft bg-bg_primary">
              <img 
                src="https://images.unsplash.com/photo-1461468611824-46457c0e11fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxjYWxtJTIwd2VsbG5lc3MlMjBuYXR1cmUlMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzgxNjMwOTEzfDA&ixlib=rb-4.1.0&q=85" 
                alt="Calm Wellness Vision Lifestyle"
                className="w-full h-full object-cover grayscale-[15%] brightness-[95%]"
              />
            </div>
          </div>

          <div className="md:col-span-7 space-y-8">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9B84] block">Our Inner Belief</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-normal tracking-tight text-text_primary leading-tight">
              A beverages brand designed for functional outcomes.
            </h2>
            <div className="space-y-6 text-base md:text-lg text-text_secondary leading-relaxed">
              <p>
                {"We believe what we consume should act as a subtle catalyst for mental and physiological alignment. InnerAura was founded on a simple vision: shifting away from overly sweetened energy cocktails towards clean, natural, scientifically-formulated elixirs."}
              </p>
              <p>
                {"As we prepare to finalize our target audience, flavor profiles, and first state-level pilots, we invite you to align with us on this journey. Because optimal wellness shouldn't feel like a chore—it should be as natural as taking a refreshing sip."}
              </p>
            </div>
            <div className="flex gap-4 pt-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-accent_primary/20 flex items-center justify-center">
                  <UserCheck className="h-4 w-4 text-accent_primary" />
                </div>
                <span className="text-sm font-semibold text-text_primary">100% Natural & Non-GMO</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-accent_primary/20 flex items-center justify-center">
                  <Layers className="h-4 w-4 text-accent_primary" />
                </div>
                <span className="text-sm font-semibold text-text_primary">Scientifically Proportioned</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer / Massive Waitlist */}
      <footer id="join" className="bg-[#E9E1D6] py-24 md:py-32 border-t border-border_soft/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center space-y-8">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8A9B84] block">Begin the alignment</span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-headings font-light text-text_primary leading-tight max-w-4xl mx-auto">
            Ready to Elevate Your Inner Well-Being? <br />
            <span className="font-normal italic text-[#5C6B5A]">Join the Aura List.</span>
          </h2>
          <p className="text-base md:text-lg text-text_secondary max-w-xl mx-auto">
            Secure early VIP access to InnerAura. Receive preview samples, early batch updates, and priority invitations to our wellness community launch events.
          </p>

          <form onSubmit={handleWaitlistSubmit} className="max-w-xl mx-auto space-y-4 pt-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                placeholder="Enter your email to align..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="waitlist-email-input"
                className="flex-grow bg-[#FBF7F0] rounded-full border border-border_soft px-6 py-4 text-text_primary placeholder:text-text_secondary/50 focus:outline-none focus:ring-2 focus:ring-accent_primary/30 transition-all text-base"
              />
              <button
                type="submit"
                disabled={isLoading}
                data-testid="waitlist-submit-button"
                className="bg-[#2A3B28] hover:bg-[#1C281B] text-white font-medium rounded-full px-8 py-4 transition-all duration-300 ease-out shadow-md hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isLoading ? "Joining..." : "Join the Aura List"}
                <ArrowRight className="h-4 w-4 text-accent_secondary" />
              </button>
            </div>
            <p className="text-xs text-text_muted">
              By joining, you agree to our earthy-friendly terms. No spam, just wellness updates.
            </p>
          </form>

          <div className="pt-16 border-t border-border_soft/60 flex flex-col sm:flex-row items-center justify-between gap-6 text-sm text-text_secondary">
            <div>
              <p className="font-headings text-lg font-normal tracking-tight text-text_primary">InnerAura</p>
              <p className="text-xs text-text_muted mt-1">© 2026 InnerAura Co. All rights reserved.</p>
            </div>
            <div className="flex gap-8">
              <a href="#science" className="hover:text-text_primary">Science</a>
              <a href="#vision" className="hover:text-text_primary">Vision</a>
              <a href="#ingredients" className="hover:text-text_primary">Ingredients</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Admin Dashboard Component
function AdminDashboard() {
  const { logout } = useAuth();
  const [waitlist, setWaitlist] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [refreshCount, setRefreshCount] = useState(0);

  // Define fetchWaitlist inside useEffect to comply with react-hooks/set-state-in-effect and dependency rules
  useEffect(() => {
    const fetchWaitlist = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API}/waitlist`);
        setWaitlist(response.data);
      } catch (err) {
        toast.error("Failed to load waitlist entries.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchWaitlist();
  }, [refreshCount]);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const response = await axios.put(`${API}/waitlist/${id}`, {
        status: newStatus
      });
      if (response.status === 200) {
        toast.success(`Entry updated to ${newStatus}`);
        setRefreshCount(prev => prev + 1);
      }
    } catch (err) {
      toast.error("Failed to update status.");
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this waitlist entry?")) return;
    try {
      const response = await axios.delete(`${API}/waitlist/${id}`);
      if (response.status === 200) {
        toast.success("Waitlist entry deleted.");
        setRefreshCount(prev => prev + 1);
      }
    } catch (err) {
      toast.error("Failed to delete entry.");
    }
  };

  const handleExportCsv = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `inneraura_waitlist_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Waitlist exported as CSV.");
    } catch (err) {
      toast.error("Failed to export waitlist.");
    }
  };

  const filteredEntries = waitlist.filter(entry => {
    if (filter === "all") return true;
    return entry.status.toLowerCase() === filter.toLowerCase();
  });

  return (
    <div className="min-h-screen bg-[#F4EFE7] font-body text-[#2A3B28] p-6 md:p-12">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Navigation back */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[#8A9A86] flex items-center justify-center text-white">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-headings font-normal tracking-tight">InnerAura Admin Control</h1>
              <p className="text-xs text-[#5C6B5A]">Real-time waitlist and outcome preference tracking</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={handleExportCsv}
              data-testid="admin-export-csv-button"
              className="bg-[#FBF7F0] border-[#D7CCBE] hover:bg-[#E9E1D6] text-[#2A3B28] rounded-full px-4"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setRefreshCount(prev => prev + 1)}
              data-testid="admin-refresh-button"
              className="bg-[#FBF7F0] border-[#D7CCBE] hover:bg-[#E9E1D6] text-[#2A3B28] rounded-full px-4"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={logout}
              data-testid="admin-logout-button"
              className="bg-[#2A3B28] hover:bg-[#1C281B] text-white rounded-full px-6"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Dashboard stats bento cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-[#FBF7F0] p-6 rounded-2xl border border-[#D7CCBE] shadow-sm">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#8A9B84] block mb-1">Total Aura List</span>
            <span className="text-3xl font-headings font-bold text-[#2A3B28]">{waitlist.length}</span>
            <span className="text-xs text-[#5C6B5A] block mt-1">Eager wellness seekers</span>
          </div>

          <div className="bg-[#FBF7F0] p-6 rounded-2xl border border-[#D7CCBE] shadow-sm">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#8A9B84] block mb-1">🧠 Focus (Nootropics)</span>
            <span className="text-3xl font-headings font-bold text-[#2A3B28]">
              {waitlist.filter(e => e.outcome_interest === "Nootropic/Focus").length}
            </span>
            <span className="text-xs text-[#5C6B5A] block mt-1">Primary outcome preference</span>
          </div>

          <div className="bg-[#FBF7F0] p-6 rounded-2xl border border-[#D7CCBE] shadow-sm">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#8A9B84] block mb-1">🌿 Calm (Stress Relief)</span>
            <span className="text-3xl font-headings font-bold text-[#2A3B28]">
              {waitlist.filter(e => e.outcome_interest === "Stress Relief/Calm").length}
            </span>
            <span className="text-xs text-[#5C6B5A] block mt-1">Primary outcome preference</span>
          </div>

          <div className="bg-[#FBF7F0] p-6 rounded-2xl border border-[#D7CCBE] shadow-sm">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#8A9B84] block mb-1">✨ Mood (Emotional)</span>
            <span className="text-3xl font-headings font-bold text-[#2A3B28]">
              {waitlist.filter(e => e.outcome_interest === "Mood Elevation").length}
            </span>
            <span className="text-xs text-[#5C6B5A] block mt-1">Primary outcome preference</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 bg-[#E9E1D6] p-1.5 rounded-full max-w-md border border-[#D7CCBE]">
          {["all", "waitlisted", "contacted", "approved"].map(tab => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`flex-grow capitalize text-xs font-semibold px-4 py-2 rounded-full transition-all ${
                filter === tab 
                  ? "bg-[#2A3B28] text-white shadow-sm" 
                  : "text-[#5C6B5A] hover:bg-[#F1EDE4]"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Data Table Container */}
        <div className="bg-[#FBF7F0] rounded-2xl border border-[#D7CCBE] overflow-hidden shadow-sm">
          <div className="p-6 border-b border-[#D7CCBE] bg-[#E9E1D6]/60 flex items-center justify-between">
            <h2 className="text-lg font-headings font-semibold text-[#2A3B28]">Waitlist Registry Data</h2>
            <span className="text-xs text-[#5C6B5A]">Showing {filteredEntries.length} entries</span>
          </div>

          <div className="p-4 overflow-x-auto">
            {isLoading ? (
              <div className="py-20 text-center text-[#5C6B5A] flex flex-col items-center gap-2">
                <RefreshCw className="h-6 w-6 animate-spin text-[#8A9A86]" />
                <span>Synchronizing waitlist dataset...</span>
              </div>
            ) : filteredEntries.length === 0 ? (
              <div className="py-20 text-center text-[#5C6B5A]">
                No waitlist entries found matching this status filter.
              </div>
            ) : (
              <Table data-testid="admin-waitlist-table">
                <TableHeader className="border-b border-[#D7CCBE] text-left">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-semibold text-[#2A3B28] p-4 text-left">Email Address</TableHead>
                    <TableHead className="font-semibold text-[#2A3B28] p-4 text-left">Outcome Preference</TableHead>
                    <TableHead className="font-semibold text-[#2A3B28] p-4 text-left">Status</TableHead>
                    <TableHead className="font-semibold text-[#2A3B28] p-4 text-left">Date Joined</TableHead>
                    <TableHead className="font-semibold text-[#2A3B28] p-4 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry, idx) => {
                    const entryId = entry.id || entry._id || `entry-${idx}`;
                    return (
                      <TableRow key={entryId} className="border-b border-[#D7CCBE]/60 hover:bg-[#F4EFE7]/40">
                        <TableCell className="p-4 font-medium font-body text-[#2A3B28]">{entry.email}</TableCell>
                        <TableCell className="p-4">
                          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-[#E9E1D6] text-[#2A3B28]">
                            {entry.outcome_interest}
                          </span>
                        </TableCell>
                        <TableCell className="p-4">
                          <select
                            value={entry.status}
                            onChange={(e) => handleUpdateStatus(entryId, e.target.value)}
                            className="bg-[#F1EDE4] border border-[#D7CCBE] text-[#2A3B28] rounded-md px-2 py-1 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-[#8A9A86]"
                          >
                            <option value="Waitlisted">Waitlisted</option>
                            <option value="Contacted">Contacted</option>
                            <option value="Approved">Approved</option>
                          </select>
                        </TableCell>
                        <TableCell className="p-4 text-xs text-[#5C6B5A]">
                          {new Date(entry.timestamp).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </TableCell>
                        <TableCell className="p-4 text-center">
                          <button
                            onClick={() => handleDeleteEntry(entryId)}
                            className="text-[#8A9B84] hover:text-red-600 transition-colors p-2 rounded-full hover:bg-red-50"
                            title="Delete entry"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// Router and Main Wrapper
function App() {
  return (
    <div className="App selection:bg-[#8A9A86] selection:text-white">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      {/* Toast Alert Toaster styled organic */}
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}

export default App;
